import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API_BASE } from "@/lib/api";
import { FileText, QrCode } from "lucide-react";

const inr = (n) => `₹${Math.abs(Number(n || 0)).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;

export default function PublicKhata() {
  const { token } = useParams();
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { fetch(`${API_BASE}/khata-public/${token}`).then(r => r.ok ? r.json() : Promise.reject(r)).then(setD).catch(() => setErr("This link is invalid or has been reset.")); }, [token]);
  if (err) return <div className="min-h-screen grid place-items-center text-sm text-zinc-500" data-testid="public-khata-error">{err}</div>;
  if (!d) return <div className="min-h-screen grid place-items-center text-xs uppercase tracking-[0.2em] text-zinc-500">Loading…</div>;
  const bal = d.balance;
  return (
    <div className="min-h-screen bg-zinc-50" data-testid="public-khata">
      <div className="bg-zinc-900 text-white">
        <div className="max-w-2xl mx-auto px-5 py-6">
          <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 font-semibold">{d.business}</div>
          <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mt-1">{d.party.name}</h1>
          <div className="mt-4 flex items-end justify-between gap-4">
            <div>
              <div className="text-[10px] tracking-[0.14em] uppercase text-zinc-400">{bal > 0 ? "You have to pay" : bal < 0 ? `${d.business} owes you` : "Account"}</div>
              <div data-testid="public-balance" className={`font-heading font-black text-4xl tabular ${bal > 0 ? "text-amber-300" : bal < 0 ? "text-emerald-300" : ""}`}>{bal === 0 ? "Settled" : inr(bal)}</div>
            </div>
            <a data-testid="public-statement" href={`${API_BASE}/khata-public/${token}/statement.pdf`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 border border-zinc-600 hover:bg-white hover:text-zinc-900 transition-colors"><FileText size={13} /> Statement PDF</a>
          </div>
        </div>
      </div>
      <div className="max-w-2xl mx-auto px-5 py-6 space-y-5">
        {d.qr_url ? (
          <section className="bg-white border border-zinc-200 p-5 flex flex-col sm:flex-row items-center gap-5" data-testid="public-pay">
            <img data-testid="public-qr" src={`${API_BASE}${d.qr_url.replace("/api", "")}`} alt="UPI QR" className="w-40 h-40" />
            <div className="flex-1 text-center sm:text-left">
              <div className="font-heading font-bold text-lg flex items-center gap-2 justify-center sm:justify-start"><QrCode size={18} /> Pay {inr(bal)} via UPI</div>
              <div className="text-sm text-zinc-600 mt-1">Scan with any UPI app (GPay, PhonePe, Paytm) or tap the button on your phone.</div>
              <div className="text-xs text-zinc-500 mt-1">UPI ID: <b className="text-zinc-900">{d.upi_id}</b></div>
              <a data-testid="public-upi-btn" href={d.upi_intent} className="inline-block mt-3 px-4 py-2 bg-zinc-900 text-white text-sm font-semibold hover:bg-zinc-700">Pay now with UPI</a>
            </div>
          </section>
        ) : null}
        <section className="bg-white border border-zinc-200" data-testid="public-entries">
          <div className="grid grid-cols-[1fr_90px_90px_100px] gap-2 px-3 py-2 bg-zinc-50 text-[10px] tracking-[0.12em] uppercase text-zinc-500 font-semibold"><span>Entry</span><span className="text-right">Given</span><span className="text-right">Paid</span><span className="text-right">Balance</span></div>
          {d.entries.map((e, i) => (
            <div key={i} className="grid grid-cols-[1fr_90px_90px_100px] gap-2 px-3 py-2.5 border-t border-zinc-100 items-center text-sm">
              <div className="min-w-0"><div className="truncate">{e.note || (e.kind === "gave" ? "Credit" : "Payment")}</div><div className="text-[11px] text-zinc-500 tabular">{e.date}{e.due_date ? <span className={e.overdue ? " text-rose-700 font-semibold" : ""}> · due {e.due_date}</span> : null}</div></div>
              <div className="text-right tabular text-rose-700">{e.kind === "gave" ? inr(e.amount) : ""}</div>
              <div className="text-right tabular text-emerald-700">{e.kind === "got" ? inr(e.amount) : ""}</div>
              <div className="text-right tabular text-xs text-zinc-600">{e.running_balance < 0 ? "−" : ""}{inr(e.running_balance)}</div>
            </div>
          ))}
          {!d.entries.length && <div className="px-3 py-8 text-center text-sm text-zinc-500">No entries yet.</div>}
        </section>
        <div className="text-center text-[10px] tracking-[0.14em] uppercase text-zinc-400">Powered by Mind Maker 369</div>
      </div>
    </div>
  );
}
